import { useCallback } from "react";
import { ExerciseResult } from "@/types";
import { useUserExercises } from "@/data/exercises.ts";
import { getScore, IELTS_SCORE, READING_SCORE } from "@/utils/exercises.ts";

interface UseCorrectionReturn {
  getCorrection: (id: string) => ExerciseResult | undefined;
}

const normalizeText = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[‘’´`]/g, "'")
    .replace(/\s+([?.,!])/g, "$1")
    .replace(/([?!,])\s+/g, "$1")
    .replace(/\.$/, "")
    .replace(/\s{2,}/g, " ")
    .trim();

const useCorrection = (): UseCorrectionReturn => {
  const { exercises } = useUserExercises();

  const getCorrection = useCallback(
    (id: string): ExerciseResult | undefined => {
      if (!id || !exercises || !exercises.done) return;

      const ex = exercises.done.find((e) => e.token_ex === id);

      if (!ex) return;

      if (ex.user_submit && ex.user_submit.date_completion) {
        delete ex.user_submit.date_completion;
      }

      const exercise: ExerciseResult = {
        ...ex,
        score: 0,
        total: ex.questions ? Object.keys(ex.questions).length : 0,
        result: {},
        ielts_score: 0,
      };

      if (!["Writing", "Mock test"].includes(ex.type)) {
        if (ex.questions) {
          Object.keys(ex.questions).forEach((key: string) => {
            const question = ex.questions[key];
            const userAnswerItem = ex.user_submit ? ex.user_submit[key] : null;

            if (
              userAnswerItem &&
              typeof userAnswerItem !== "string" &&
              userAnswerItem.user_answer
            ) {
              const normalizedCorrectAnswer = normalizeText(
                question.correct_answer,
              );

              const normalizedUserAnswer = normalizeText(
                userAnswerItem.user_answer,
              );

              exercise.result[key] = {
                user_answer: normalizedUserAnswer,
                question_type: question.question_type,
                correct_answer: normalizedCorrectAnswer,
              };

              if (normalizedCorrectAnswer === normalizedUserAnswer) {
                exercise.score++;
              }
            } else {
              exercise.result[key] = {
                user_answer: "N/A",
                question_type: question.question_type,
                correct_answer: normalizeText(question.correct_answer),
              };
            }
          });
        }

        const score = Number(String(ex.user_score).split("/")[0]);

        exercise.ielts_score = getScore(
          score,
          ex.type === "Reading" ? READING_SCORE : IELTS_SCORE,
        );
      }

      return exercise;
    },
    [exercises],
  );

  return { getCorrection };
};

export default useCorrection;
